import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@components/Button";
import { parseHtml } from "@components/HTMLParser/utils";
import { BinIcon } from "@components/icons";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useRemoveCustomTransactionCategoryMutation } from "@services/budget/mutations";
import { useGetExpensePlansQuery } from "@services/budget/queries";
import {
  ExpensePlanState,
  RemoveBudgetTransactionCategoryType,
  type IGetCustomTransactionCategoryResponse,
} from "@services/budget/types";

interface IRemoveBudgetTransactionCategoryWithExpensePlanModalProps
  extends IUseModalProps {
  transactionCategoryData: IGetCustomTransactionCategoryResponse;
  onSubmit?: () => void;
}

export const RemoveBudgetTransactionCategoryWithExpensePlanModal: React.FC<
  IRemoveBudgetTransactionCategoryWithExpensePlanModalProps
> = (props) => {
  const { transactionCategoryData, onSubmit, ...rest } = props;
  const modal = useModal(rest);
  const { t } = useTranslation();

  const { mutateAsync: removeCustomTransactionCategory, isPending } =
    useRemoveCustomTransactionCategoryMutation();

  const { toastApiError } = useApiErrorUtils();

  const { data: expensePlans } = useGetExpensePlansQuery(
    {
      expensePlanStateId: ExpensePlanState.Active,
    },
    false,
  );

  const submit = async () => {
    if (onSubmit) {
      onSubmit();
      modal.close();
    } else {
      await removeCustomTransactionCategory({
        transactionCategoryId: transactionCategoryData.transactionCategoryId,
        removeType: RemoveBudgetTransactionCategoryType.NoAction,
      })
        .then(modal.close)
        .catch(toastApiError);
    }
  };

  return (
    <Modal
      modal={modal}
      badgeProps={{
        icon: BinIcon,
        color: "red",
      }}
      title={t(
        "budget.removeBudgetTransactionCategoryWithExpensePlanModal.title",
        {
          name: transactionCategoryData.categoryName,
        },
      )}
      subtitle={parseHtml(
        t(
          "budget.removeBudgetTransactionCategoryWithExpensePlanModal.subtitle",
          {
            name: transactionCategoryData.categoryName,
            expensePlanNames: expensePlans?.expensePlans
              .map((plan) => plan.planName)
              .join(", "),
          },
        ),
      )}
      actionButtons={
        <React.Fragment>
          <Button variant="secondary" className="w-full" onClick={modal.close}>
            {t(
              "budget.removeBudgetTransactionCategoryWithExpensePlanModal.cancel",
            )}
          </Button>
          <Button className="w-full" onClick={submit} isLoading={isPending}>
            {t(
              "budget.removeBudgetTransactionCategoryWithExpensePlanModal.submit",
            )}
          </Button>
        </React.Fragment>
      }
    ></Modal>
  );
};
